// Calling getenv without a default makes the variable boot-fatal: the bundle
// throws at module load if it was built without it. When adding one, also add
// it to REQUIRED_VITE_VARS in build-support/src/build-frontend.sh (which fails
// the build instead of shipping a dead bundle) and to every
// infra/.../customers/*/.viteBuild.env.
function getenv<T>(name: string, defaultValue?: T): T {
  const value = import.meta.env[name] as T;
  if (value === undefined) {
    if (defaultValue !== undefined) {
      return defaultValue;
    }
    throw new Error(`Required environment variable ${name} is not defined`);
  }
  return value;
}

export interface SSOSignInConfig {
  cognitoProviderId: string;
  displayName: string;
}

function getSsoSignInConfig(): SSOSignInConfig | null {
  const serialized = getenv<string | null>("VITE_SSO_SIGN_IN_CONFIG", null);
  if (!serialized) {
    return null;
  }

  try {
    return JSON.parse(serialized) as SSOSignInConfig;
  } catch {
    return null;
  }
}

export class Env {
  public static readonly appEnv = getenv<string>("VITE_REACT_APP_ENV");
  public static readonly devBranchName = getenv<string>("VITE_BRANCH_NAME", "");

  /** Path prefix used by branch-per-URL deploys (e.g. ``/feat-feedback``).
   *
   * Empty string when no dev branch is set. Use this anywhere a URL builder
   * needs to honour the basename — keeps the ``Env.devBranchName ? ...``
   * conditional in one place rather than duplicated per call site. */
  public static get appBasePath(): string {
    return Env.devBranchName ? `/${Env.devBranchName}` : "";
  }
  public static readonly robotoServiceBaseUrl = getenv<string>("VITE_API_URL");
  /** Where the public documentation lives, without a trailing slash.
   *
   * Build a page's URL with ``docsUrl("/learn/secrets")`` rather than
   * concatenating onto this directly. */
  public static readonly docsBaseUrl = getenv<string>(
    "VITE_DOCS_BASE_URL",
    "https://www.roboto.ai/docs",
  );
  public static readonly robotoMapboxToken =
    getenv<string>("VITE_MAPBOX_TOKEN");

  public static readonly sentryDsn = getenv<string>("VITE_SENTRY_DSN");
  public static readonly PostHogPublicToken = getenv<string>(
    "VITE_POSTHOG_API_KEY",
  );
  public static readonly enableGoogleSignIn =
    getenv<string>("VITE_ENABLE_GOOGLE_SIGNIN", "true") === "true";
  /** Whether this deployment serves the Slack integration. Defaults on. */
  public static readonly slackEnabled =
    getenv<string>("VITE_ENABLE_SLACK", "true") === "true";
  public static readonly ssoSignInConfig: SSOSignInConfig | null =
    getSsoSignInConfig();
  public static readonly gitCommitId = getenv<string>(
    "VITE_GIT_COMMIT_ID",
    "unknown",
  );
  public static readonly gitCommitDate = getenv<string>(
    "VITE_GIT_COMMIT_DATE",
    "unknown",
  );
}
